<template>
  <div class="waterbody-quick-filters">
    <all-filters-button rounded />
    <div class="quick-filter-triggers">
      <filter-dropdown-trigger
        v-for="(filter, index) in currentFilters"
        :key="filter.type"
        :is-active="isActiveFilterTrigger(index)"
        :filter-has-value="isFilterActive(filter.type)"
        :label="filter.label"
        @toggle="handleDropdownToggled($event, index)"
      />
    </div>
    <div class="quick-filter-dropdowns">
      <animation-wrap :type="ANIMATION_TYPES.FADE_TRANSLATE" mode="out-in">
        <quick-filter-species
          v-if="isActiveFilterDropdown(FILTER_KEYS[FIELDS.FISH_TYPES])"
        />
        <quick-filter-access
          v-else-if="isActiveFilterDropdown(FILTER_KEYS[FIELDS.AMENITIES])"
        />
      </animation-wrap>
    </div>
  </div>
</template>
<script setup lang="ts">
///// imports /////
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import allFiltersButton from '@/components/activity-filtering/all-filters-button.vue'
import filterDropdownTrigger from '@/components/activity-filtering/filter-dropdown-trigger.vue'
import animationWrap from '@/components/shared/animation-wrap.vue'
import quickFilterSpecies from '@/fish/components/filtering/quick-filter-species.vue'
import quickFilterAccess from '@/fish/components/filtering/quick-filter-access.vue'

import { getCurrentActiveFilters } from '@/services/filter-layer-service'
import { setMobileMenuOpen } from '@/services/control-layout-service'
import { FILTER_KEYS } from '@/services/filter-layer-service/constants'
import marketingEventService from '@/services/marketing-event-service'

import { FIELDS, SLUGS } from '@/fish/lib/taxonomy-constants'
import browser from '@/lib/browser'
import { ANIMATION_TYPES } from '@/lib/enums'
import {
  getModeQuickFilters,
  APPLY_QUICK_FILTER_PUBSUB,
} from '@/lib/multi-activity-experience'
import pubsub from '@/lib/pubsub'
import { EVENTS, CARD_ORIGINS } from '@/lib/enums-marketing'

import routeNames from '@/fish/router/constants'

///// refs and variables /////
const route = useRoute()

///// computed /////
const currentFilters = computed(() => getModeQuickFilters())
const activeFilterIndex = ref<number | null>(null)
const activeFilter = computed(() => {
  return typeof activeFilterIndex.value === 'number'
    ? currentFilters.value?.[activeFilterIndex.value]
    : null
})

const activeFilters = computed(() => {
  return getCurrentActiveFilters() || []
})

///// functions /////
const isActiveFilterTrigger = (index: number) => {
  return activeFilterIndex.value === index
}

const isFilterActive = (filterType: string) => {
  // The "Access" filter displays the boat launch filter, which is a subtype of amenities filters
  // Need a little extra handling for that
  if (filterType === FILTER_KEYS[FIELDS.AMENITIES]) {
    return activeFilters.value.includes(FILTER_KEYS[SLUGS.LAUNCH])
  }
  return activeFilters.value.includes(filterType)
}

const isActiveFilterDropdown = (type: string) => {
  return activeFilter.value?.type === type
}

const sendMarketingEvent = (index: number) => {
  const origin =
    route.name === routeNames.WATERBODIES
      ? CARD_ORIGINS.DISCOVER
      : CARD_ORIGINS.MAP
  const type = currentFilters.value?.[index].label.toLowerCase()
  marketingEventService.publish(EVENTS.FILTER_CARD_OPENED, {
    origin,
    type,
  })
}

const handleDropdownToggled = (toggleState: boolean, dropdownIndex: number) => {
  activeFilterIndex.value = toggleState ? dropdownIndex : null
  if (browser.isMobile()) {
    setMobileMenuOpen(false)
  }
  if (toggleState) {
    sendMarketingEvent(dropdownIndex)
  }
}

const disableAllQuickFilters = () => {
  activeFilterIndex.value = null
}

pubsub.subscribe(APPLY_QUICK_FILTER_PUBSUB, () => {
  disableAllQuickFilters()
})
///// watchers /////
watch(currentFilters, () => {
  disableAllQuickFilters()
})
</script>
<style lang="scss" scoped>
@use '@/styles/_layer-map.scss';
@use '@/styles/system/_breakpoints.scss';

.waterbody-quick-filters {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--ys-spacing-100);
  z-index: layer-map.$z-index-level-controls;
  @include breakpoints.mobile {
    // Below side nav, above controls at next level down
    z-index: calc(layer-map.$z-index-level-side-nav - 1);
  }
}
.quick-filter-triggers {
  display: flex;
  gap: var(--ys-spacing-100);
  width: 100%;
}
.quick-filter-dropdowns {
  position: absolute;
  left: 0;
  top: calc(100% + 8px);
  width: 100%;
}
</style>
